import React, { useState, useEffect, useRef } from 'react';
import { X, Flame, Waves, Info, CloudOff, Trash2, Bell, CheckCheck } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { authFetch, API } from '../utils/api';


const ICON_MAP = {
  fire: { icon: Flame, cls: 'text-orange-400 bg-orange-400/10' },
  wave: { icon: Waves, cls: 'text-primary bg-primary/10' },
  flat: { icon: CloudOff, cls: 'text-slate-400 bg-slate-400/10' },
  info: { icon: Info, cls: 'text-blue-400 bg-blue-400/10' },
  streak: { icon: Flame, cls: 'text-amber-400 bg-amber-400/10' },
};

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function NotificationsPanel({ open, onClose }) {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const panelRef = useRef(null);

  const loadNotifications = async () => {
    try {
      const res = await authFetch(`${API}/api/notifications`);
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
        setUnread(data.unread || 0);
      }
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => {
    if (open) loadNotifications();
  }, [open]);

  // Also load on mount to get unread count
  useEffect(() => { loadNotifications(); }, []);

  const handleDismiss = async (notifId) => {
    setNotifications(prev => prev.filter(n => n.notif_id !== notifId));
    await authFetch(`${API}/api/notifications/${notifId}`, { method: 'DELETE',  });
  };

  const handleClearAll = async () => {
    setNotifications([]);
    setUnread(0);
    await authFetch(`${API}/api/notifications`, { method: 'DELETE',  });
  };

  const handleMarkAllRead = async () => {
    const unreadNotifs = notifications.filter(n => !n.read);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnread(0);
    for (const n of unreadNotifs) {
      authFetch(`${API}/api/notifications/${n.notif_id}/read`, { method: 'POST',  });
    }
  };

  // Refresh when opened
  useEffect(() => {
    if (open) loadNotifications();
  }, [open]);

  return (
    <>
      {/* Unread badge (always visible, rendered by parent) */}
      {/* Panel */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[99]"
              onClick={onClose}
            />
            <motion.div
              ref={panelRef}
              initial={{ opacity: 0, y: -8, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.97 }}
              transition={{ duration: 0.15 }}
              className="absolute inset-x-0 bg-bg-paper border border-white/10 rounded-b-3xl shadow-2xl z-[100] overflow-hidden"
              style={{ maxHeight: 420, top: 52 }}
              data-testid="notifications-panel"
            >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
              <div className="flex items-center gap-2">
                <Bell className="w-3.5 h-3.5 text-primary" />
                <span className="text-xs font-bold">Alerts</span>
                {unread > 0 && (
                  <span className="text-[9px] font-bold bg-primary/20 text-primary px-1.5 py-0.5 rounded-full" data-testid="notif-unread-count">
                    {unread}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5">
                {notifications.length > 0 && (
                  <>
                    <button
                      onClick={handleMarkAllRead}
                      className="text-[9px] text-muted-foreground/50 hover:text-primary px-1.5 py-1 rounded-md hover:bg-white/5 transition-colors flex items-center gap-1"
                      data-testid="notif-mark-all-read-btn"
                    >
                      <CheckCheck className="w-3 h-3" />
                    </button>
                    <button
                      onClick={handleClearAll}
                      className="text-[9px] text-muted-foreground/50 hover:text-red-400 px-1.5 py-1 rounded-md hover:bg-white/5 transition-colors flex items-center gap-1"
                      data-testid="notif-clear-all-btn"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </>
                )}
                <button onClick={onClose} className="p-1 hover:bg-white/5 rounded-md">
                  <X className="w-3.5 h-3.5 text-muted-foreground/40" />
                </button>
              </div>
            </div>

            {/* List */}
            <div className="overflow-y-auto" style={{ maxHeight: 360 }}>
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                </div>
              ) : notifications.length === 0 ? (
                <div className="text-center py-10 px-4" data-testid="notif-empty">
                  <Waves className="w-6 h-6 text-muted-foreground/20 mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground/40">no alerts yet</p>
                  <p className="text-[10px] text-muted-foreground/25 mt-0.5">trip forecasts & crew updates will show here</p>
                </div>
              ) : (
                <div>
                  {notifications.map((notif, i) => {
                    const iconInfo = ICON_MAP[notif.icon] || ICON_MAP.info;
                    const IconComp = iconInfo.icon;
                    return (
                      <motion.div
                        key={notif.notif_id}
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.03 }}
                        className={`flex items-start gap-3 px-4 py-3 border-b border-white/[0.03] last:border-0 transition-colors ${
                          !notif.read ? 'bg-primary/[0.02]' : ''
                        }`}
                        data-testid={`notif-item-${notif.notif_id}`}
                      >
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${iconInfo.cls}`}>
                          <IconComp className="w-3.5 h-3.5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className={`text-xs font-semibold leading-tight ${!notif.read ? 'text-foreground' : 'text-muted-foreground'}`}>
                              {notif.title}
                            </p>
                            {!notif.read && (
                              <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0 mt-1" />
                            )}
                          </div>
                          <p className="text-[11px] text-muted-foreground/60 mt-0.5 leading-relaxed">{notif.body}</p>
                          <div className="flex items-center justify-between mt-1.5">
                            <span className="text-[9px] text-muted-foreground/30">{timeAgo(notif.created_at)}</span>
                            <button
                              onClick={() => handleDismiss(notif.notif_id)}
                              className="text-[9px] text-muted-foreground/30 hover:text-red-400 transition-colors flex items-center gap-0.5 px-1"
                              data-testid={`notif-dismiss-${notif.notif_id}`}
                            >
                              <X className="w-2.5 h-2.5" />
                              clear
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>
          </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

// Export unread count hook for use in Home
export function useUnreadCount() {
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    const load = async () => {
      try {
        const res = await authFetch(`${API}/api/notifications`);
        if (res.ok) {
          const data = await res.json();
          setUnread(data.unread || 0);
        }
      } catch {}
    };
    load();
    const interval = setInterval(load, 60000); // refresh every minute
    return () => clearInterval(interval);
  }, []);
  return [unread, setUnread];
}
